/**
 * What every library in this app is FOR.
 *
 * DECLARED, not derived — the version and the presence of a package come from
 * package.json at runtime, but no machine can tell you why it is here. That is
 * this file's whole job.
 *
 * `stack.test.ts` fails if package.json gains a dependency that is not listed
 * here. Adding a library therefore costs one line of explanation, which is the
 * cheapest moment to ask whether it is worth adding at all.
 */
import type { StackEntry } from './types.ts'

export const stack: StackEntry[] = [
  // Framework
  {
    name: 'react',
    category: 'Framework',
    role: 'The UI library everything else is built on.',
    docs: 'https://react.dev',
  },
  {
    name: 'react-dom',
    category: 'Framework',
    role: 'Renders React into the browser.',
    docs: 'https://react.dev',
  },

  // Routing & data
  {
    name: '@tanstack/react-router',
    category: 'Routing & data',
    role: 'File-based routing, and the guard that redirects a signed-out visitor.',
    docs: 'https://tanstack.com/router',
  },
  {
    name: '@tanstack/react-query',
    category: 'Routing & data',
    role: 'Caches server data and handles loading, retry and refetch.',
    docs: 'https://tanstack.com/query',
  },
  {
    name: 'date-fns',
    category: 'Routing & data',
    role: 'Formats and compares dates. The one date library — never add another.',
    docs: 'https://date-fns.org',
  },
  {
    name: 'zod',
    category: 'Routing & data',
    role: 'Validates everything crossing a boundary: RPC input, env vars, webhooks.',
    docs: 'https://zod.dev',
  },

  // API
  {
    name: '@orpc/server',
    category: 'API',
    role: 'Defines the server procedures the app calls.',
    docs: 'https://orpc.unnoq.com',
  },
  {
    name: '@orpc/client',
    category: 'API',
    role: 'The typed client. The UI never calls fetch directly.',
    docs: 'https://orpc.unnoq.com',
  },
  {
    name: '@orpc/tanstack-query',
    category: 'API',
    role: 'Turns each procedure into a React Query hook.',
    docs: 'https://orpc.unnoq.com',
  },
  {
    name: '@orpc/openapi',
    category: 'API',
    role: 'Publishes the same procedures as a REST/OpenAPI surface.',
    docs: 'https://orpc.unnoq.com',
  },
  {
    name: '@orpc/zod',
    category: 'API',
    role: 'Lets the OpenAPI schema be generated from the Zod schemas.',
    docs: 'https://orpc.unnoq.com',
  },
  {
    name: 'hono',
    category: 'API',
    role: 'The small HTTP server everything is mounted on.',
    docs: 'https://hono.dev',
  },
  {
    name: '@hono/node-server',
    category: 'API',
    role: 'Runs Hono on Node in production.',
    docs: 'https://hono.dev',
  },

  // Database
  {
    name: 'drizzle-orm',
    category: 'Database',
    role: 'Describes the tables and builds the queries, with types from the schema.',
    docs: 'https://orm.drizzle.team',
  },
  {
    name: 'pg',
    category: 'Database',
    role: 'The Postgres driver and connection pool underneath Drizzle.',
    docs: 'https://node-postgres.com',
  },

  // Accounts
  {
    name: 'better-auth',
    category: 'Accounts',
    role: 'Sign-up, sign-in, sessions, email verification, and organisations.',
    docs: 'https://better-auth.com',
  },
  {
    name: '@better-auth/drizzle-adapter',
    category: 'Accounts',
    role: 'Stores Better Auth’s own tables through Drizzle.',
    docs: 'https://better-auth.com',
  },
  {
    name: '@better-auth/oauth-provider',
    category: 'Accounts',
    role: 'Lets AI apps sign people in over OAuth 2.1 for MCP — registration, PKCE, tokens, consent (ADR mcp-server).',
    docs: 'https://www.better-auth.com/docs/plugins/oauth-provider',
  },
  {
    name: '@modelcontextprotocol/sdk',
    category: 'Accounts',
    role: 'The MCP protocol at /mcp — how AI apps (Claude, ChatGPT…) read the workspace as a person (ADR mcp-server).',
    docs: 'https://modelcontextprotocol.io',
  },

  // Interface
  {
    name: '@base-ui/react',
    category: 'Interface',
    role: 'The accessible primitives shadcn/ui is built on here — dialogs, menus, tooltips.',
    docs: 'https://base-ui.com',
  },
  {
    name: 'lucide-react',
    category: 'Interface',
    role: 'The icon set. The only one.',
    docs: 'https://lucide.dev',
  },
  {
    name: 'react-markdown',
    category: 'Interface',
    role: 'Shows the assistant’s replies as formatted text — lists, bold, tables — without ever running HTML from them (ADR markdown-replies).',
    docs: 'https://github.com/remarkjs/react-markdown',
  },
  {
    name: 'remark-gfm',
    category: 'Interface',
    role: 'Adds tables, checklists and strikethrough to the assistant’s Markdown (ADR markdown-replies).',
    docs: 'https://github.com/remarkjs/remark-gfm',
  },
  {
    name: 'fuzzysort',
    category: 'Routing & data',
    role: 'Forgiving search — finds "Henderson" from "hendrson" and "oh now" from "now". The one fuzzy-matching library (ADR fuzzysort).',
    docs: 'https://github.com/farzher/fuzzysort',
  },
  {
    name: 'yauzl',
    category: 'Routing & data',
    role: 'Opens an uploaded .xlsx (a zip) without unpacking it to disk, and checks sizes before anything is inflated (ADR xlsx-reader).',
    docs: 'https://github.com/thejoshwolfe/yauzl',
  },
  {
    name: 'saxes',
    category: 'Routing & data',
    role: 'Reads the XML inside a spreadsheet as a stream, refusing DOCTYPEs and entities (ADR xlsx-reader).',
    docs: 'https://github.com/lddubeau/saxes',
  },
  {
    name: 'cmdk',
    category: 'Interface',
    role: 'The ⌘K command palette.',
    docs: 'https://cmdk.paco.me',
  },
  {
    name: 'sonner',
    category: 'Interface',
    role: 'Toasts — a short confirmation where a result is not otherwise visible, with Undo only where something vanished.',
    docs: 'https://sonner.emilkowal.ski',
  },
  {
    name: '@tanstack/react-table',
    category: 'Interface',
    role: 'Sorting, filtering and paging for data tables.',
    docs: 'https://tanstack.com/table',
  },
  {
    name: 'recharts',
    category: 'Interface',
    role: 'Charts on the dashboard screens.',
    docs: 'https://recharts.org',
  },
  {
    name: 'react-day-picker',
    category: 'Interface',
    role: 'The calendar inside the date picker.',
    docs: 'https://daypicker.dev',
  },
  {
    name: 'class-variance-authority',
    category: 'Interface',
    role: 'Defines a component’s variants (button size, tone) in one place.',
    docs: 'https://cva.style',
  },
  {
    name: 'clsx',
    category: 'Interface',
    role: 'Joins class names conditionally.',
    docs: 'https://github.com/lukeed/clsx',
  },
  {
    name: 'tailwind-merge',
    category: 'Interface',
    role: 'Resolves conflicting Tailwind classes so a passed className actually wins.',
    docs: 'https://github.com/dcastil/tailwind-merge',
  },
  {
    name: 'cn',
    category: 'Interface',
    role: 'The import name shadcn’s generator writes; rewritten to @/lib/utils on add (ADR template-build-deviations).',
    docs: 'https://ui.shadcn.com',
  },
  {
    name: 'tw-animate-css',
    category: 'Interface',
    role: 'The enter/exit animations dialogs and sheets use.',
    docs: 'https://github.com/Wombosvideo/tw-animate-css',
  },

  // Email
  {
    name: 'resend',
    category: 'Email',
    role: 'Sends verification and invitation email. On the critical path for signup.',
    docs: 'https://resend.com/docs',
  },

  // Observability
  {
    name: 'posthog-node',
    category: 'Observability',
    role: 'Reports server crashes to PostHog, tied to the person who hit them.',
    docs: 'https://posthog.com/docs/libraries/node',
  },
  {
    name: 'pino',
    category: 'Observability',
    role: 'Structured request logs, each carrying the x-request-id you trace with.',
    docs: 'https://getpino.io',
  },
  {
    name: 'posthog-js',
    category: 'Observability',
    role: 'Product analytics. Also lazily loaded.',
    docs: 'https://posthog.com/docs',
  },

  // ——— Development only. Pruned from the production image, but part of what
  // ——— this app is built from, so they belong on the page.
  {
    name: 'vite',
    category: 'Build',
    role: 'The dev server and the production bundler.',
    docs: 'https://vite.dev',
    dev: true,
  },
  {
    name: '@vitejs/plugin-react',
    category: 'Build',
    role: 'Teaches Vite about React, including fast refresh.',
    docs: 'https://github.com/vitejs/vite-plugin-react',
    dev: true,
  },
  {
    name: '@tanstack/router-plugin',
    category: 'Build',
    role: 'Generates the route tree from the files in src/routes.',
    docs: 'https://tanstack.com/router',
    dev: true,
  },
  {
    name: '@tailwindcss/vite',
    category: 'Build',
    role: 'Compiles Tailwind during the build.',
    docs: 'https://tailwindcss.com/docs/installation/using-vite',
    dev: true,
  },
  {
    name: 'tailwindcss',
    category: 'Build',
    role: 'The styling system itself. Every token lives in src/styles/globals.css.',
    docs: 'https://tailwindcss.com',
    dev: true,
  },
  {
    name: 'esbuild',
    category: 'Build',
    role: 'Bundles the server for the production image.',
    docs: 'https://esbuild.github.io',
    dev: true,
  },
  {
    name: 'tsx',
    category: 'Build',
    role: 'Runs TypeScript directly for scripts and the dev API server.',
    docs: 'https://tsx.is',
    dev: true,
  },
  {
    name: 'concurrently',
    category: 'Build',
    role: 'Runs the web and API dev servers side by side under one command.',
    docs: 'https://github.com/open-cli-tools/concurrently',
    dev: true,
  },
  {
    name: 'vitest',
    category: 'Testing',
    role: 'Unit tests for core logic and the registries.',
    docs: 'https://vitest.dev',
    dev: true,
  },
  {
    name: '@playwright/test',
    category: 'Testing',
    role: 'Drives a real browser: the design audit and the critical-flow specs.',
    docs: 'https://playwright.dev',
    dev: true,
  },
  {
    name: '@axe-core/playwright',
    category: 'Testing',
    role: 'Checks each screen against WCAG 2.2 AA in the usability audit (pnpm ux:audit).',
    docs: 'https://github.com/dequelabs/axe-core-npm',
    dev: true,
  },
  {
    name: '@testing-library/react',
    category: 'Testing',
    role: 'Renders components in tests the way a person would use them.',
    docs: 'https://testing-library.com',
    dev: true,
  },
  {
    name: '@testing-library/jest-dom',
    category: 'Testing',
    role: 'The extra assertions for asserting about DOM state.',
    docs: 'https://github.com/testing-library/jest-dom',
    dev: true,
  },
  {
    name: 'jsdom',
    category: 'Testing',
    role: 'The fake browser Vitest runs component tests inside.',
    docs: 'https://github.com/jsdom/jsdom',
    dev: true,
  },
  {
    name: 'typescript',
    category: 'Tooling',
    role: 'The type system. Strict, with noUncheckedIndexedAccess.',
    docs: 'https://typescriptlang.org',
    dev: true,
  },
  {
    name: '@biomejs/biome',
    category: 'Tooling',
    role: 'Lint and format in one tool, instead of ESLint plus Prettier.',
    docs: 'https://biomejs.dev',
    dev: true,
  },
  {
    name: 'drizzle-kit',
    category: 'Tooling',
    role: 'Generates migrations from the schema. Dev only — the deploy runs them with drizzle-orm.',
    docs: 'https://orm.drizzle.team/kit-docs/overview',
    dev: true,
  },
  {
    name: 'drizzle-zod',
    category: 'Tooling',
    role: 'Derives Zod schemas from Drizzle tables so they cannot drift apart. Used at runtime by the sandbox data-copy import.',
    docs: 'https://orm.drizzle.team/docs/zod',
  },
  {
    name: 'shadcn',
    category: 'Tooling',
    role: 'Adds components from the registry. Always fix its cn import afterwards (ADR template-build-deviations).',
    docs: 'https://ui.shadcn.com',
    dev: true,
  },
  {
    name: 'railway',
    category: 'Tooling',
    role: 'The Railway CLI, used to deploy and to apply the infrastructure file.',
    docs: 'https://docs.railway.com/develop/cli',
    dev: true,
  },
  {
    name: 'pino-pretty',
    category: 'Tooling',
    role: 'Makes the structured logs readable in a terminal during development.',
    docs: 'https://github.com/pinojs/pino-pretty',
    dev: true,
  },
  {
    name: '@tanstack/react-query-devtools',
    category: 'Tooling',
    role: 'Inspects the query cache in development.',
    docs: 'https://tanstack.com/query',
    dev: true,
  },
  {
    name: '@tanstack/react-router-devtools',
    category: 'Tooling',
    role: 'Inspects routing and loaders in development.',
    docs: 'https://tanstack.com/router',
    dev: true,
  },
  {
    name: '@types/node',
    category: 'Tooling',
    role: 'Type definitions for Node built-ins.',
    docs: 'https://github.com/DefinitelyTyped/DefinitelyTyped',
    dev: true,
  },
  {
    name: '@types/react',
    category: 'Tooling',
    role: 'Type definitions for React.',
    docs: 'https://github.com/DefinitelyTyped/DefinitelyTyped',
    dev: true,
  },
  {
    name: '@types/react-dom',
    category: 'Tooling',
    role: 'Type definitions for React DOM.',
    docs: 'https://github.com/DefinitelyTyped/DefinitelyTyped',
    dev: true,
  },
  {
    name: '@types/pg',
    category: 'Tooling',
    role: 'Type definitions for the Postgres driver.',
    docs: 'https://github.com/DefinitelyTyped/DefinitelyTyped',
    dev: true,
  },
  {
    name: 'yazl',
    category: 'Testing',
    role: 'Builds fake .xlsx workbooks for the reader tests. Never ships (ADR xlsx-reader).',
    docs: 'https://github.com/thejoshwolfe/yazl',
    dev: true,
  },
  {
    name: '@types/yauzl',
    category: 'Tooling',
    role: 'Type definitions for the zip reader.',
    docs: 'https://github.com/DefinitelyTyped/DefinitelyTyped',
    dev: true,
  },
  {
    name: '@types/yazl',
    category: 'Tooling',
    role: 'Type definitions for the test zip writer.',
    docs: 'https://github.com/DefinitelyTyped/DefinitelyTyped',
    dev: true,
  },
]

/** Quick lookup when joining the declared role to the installed version. */
export const stackByName = new Map(stack.map((entry) => [entry.name, entry]))
